import React from "react";
import { Box, Stack } from "@mui/material";
import { tokens, displayFont, radius } from "../../../../../../../theme";

/**
 * A counter on the employee record.
 *
 * Every panel in the record book opens with a row of these — four across, an
 * Archivo figure with a label above and one line of context below. The record
 * had them on Overview only, built inline as `SnapshotCard`, so every other tab
 * dropped the reader straight into a table with nothing summarising it.
 *
 * `sub` is deliberately required-ish in spirit: a bare number on a person's
 * record ("12.5") answers nothing without "of 24 days" beside it.
 */
const RecordStat = ({ label, value, sub, tone, icon }) => (
  <Box
    sx={{
      background: tokens.card,
      border: `1px solid ${tokens.border}`,
      borderRadius: `${radius.panel}px`,
      p: "1.25rem",
      minWidth: 0,
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: "0.5rem" }}>
      {icon && (
        <Box
          sx={{
            width: "2rem",
            height: "2rem",
            borderRadius: "8px",
            background: tokens.surface,
            color: tokens.primary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: "none",
          }}
        >
          {icon}
        </Box>
      )}
      <Box
        sx={{
          fontSize: "0.6875rem",
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: tokens.textMuted,
          minWidth: 0,
        }}
      >
        {label}
      </Box>
    </Stack>
    <Box
      sx={{
        fontFamily: displayFont,
        fontSize: "1.75rem",
        fontWeight: 700,
        lineHeight: 1.1,
        fontVariantNumeric: "tabular-nums",
        color: tone || tokens.text,
      }}
    >
      {value}
    </Box>
    {sub != null && (
      <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, mt: "0.3125rem" }}>{sub}</Box>
    )}
  </Box>
);

/** The row they sit in: four across, collapsing to two and then one. */
export const RecordStatRow = ({ children, columns = 4 }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "repeat(2, minmax(0, 1fr))",
        md: `repeat(${Math.min(columns, 3)}, minmax(0, 1fr))`,
        lg: `repeat(${columns}, minmax(0, 1fr))`,
      },
      gap: "1rem",
      mb: "1.375rem",
    }}
  >
    {children}
  </Box>
);

export default RecordStat;
